import { Link } from '@inertiajs/react'
import { Wordmark } from '../components/core/Wordmark.jsx'
import { routes } from '~/marketing/routes'
import { quoted } from '~/marketing/marketingData'
import { legal } from '~/marketing/legalData'

// The one footer, on every page a customer or a visitor reaches — the public
// site, the signed-in app and the nine standalone pages (sign in, sign up,
// reset, verification, invitation, unsubscribe). The founder's call
// 2026-10-30: it is always the same, so nothing in it reads the session.
// The admin screen is the one place without it; it is ours, not a customer's.
//
// Moved here from `MarketingLayout`, where it lived alone until that day. The
// reasoning for each column is kept with it.
const foot = { fontSize: 'var(--fs-body-sm)', color: 'var(--text-body)' }
const col = { display: 'grid', gap: 'var(--space-3)', alignContent: 'start' }

export function SiteFooter() {
  return (
    <footer style={{ background: 'var(--bg-deep)', borderTop: '1px solid var(--border-subtle)' }}>
      <div className="sp-page" style={{ paddingBlock: 'var(--space-16)', display: 'grid', gap: 'var(--space-10)' }}>
        <div style={{ display: 'grid', gap: 'var(--space-10)', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))' }}>
          <div style={col}>
            {/* Larger than the header's 23 because nothing constrains it here —
                the footer has no 56px bar to sit inside. */}
            <Wordmark size={26} />
            {/*
              "local businesses", not "home services" and not "small
              businesses". Two corrections in one line.

              Not home services: the product accepts any local business and
              nothing in it gates on trade, so a footer that narrows it turns
              away customers it can already serve — a mistake this project has
              made once before and wrote down. The trade wedge is how it is
              *sold*, not what it is.

              And **local**, not small, because small is the wrong axis. Every
              prompt carries a city and every search runs *from* a place, so a
              small business with no city cannot use this at all. Locality is
              also the differentiator — no competitor tracks below country
              level — so the accurate word is the distinctive one. It matches
              the page title and the hero eyebrow, which said it already.
            */}
            <p className="sp-caption" style={{ maxWidth: '30ch' }}>AI search optimization for local businesses.</p>
          </div>
          {/*
            The Trades column is gone, and /hvac and /plumbing still exist.
            They are campaign landing pages — reached from an ad or a search
            result, which is what a landing page is for — so linking them in
            the footer advertised the wedge as if it were the product's scope.
            Two of the four entries were also not links at all: "Electrical
            SOON" and "Roofing SOON" pointed at nothing, so the column
            promised two trades that could not be clicked.
          */}
          <div style={col}>
            <span className="sp-label">Product</span>
            <Link href={routes.grader} style={foot}>Free visibility check</Link>
            <Link href={routes.pricing} style={foot}>Pricing</Link>
            <Link href={routes.methodology} style={foot}>Methodology</Link>
            <Link href={routes.faq} style={foot}>FAQ</Link>
            <Link href={routes.blog} style={foot}>Blog</Link>
          </div>
          {/*
            These were a heading reading "Terms" over two greyed spans,
            "Privacy" and "Security", none of them a link. That was the right
            call while the pages did not exist — a footer linking a route this
            app does not serve is the mistake that shipped three times
            already. The pages exist now, so they are links.

            Security is gone rather than built. There was no security page and
            no decision to write one; a greyed word promising a page nobody
            had scheduled is a commitment made by a layout. What that column
            needed was the four pages a customer and a payment reviewer
            actually look for, which is what it now has.
          */}
          <div style={col}>
            <span className="sp-label">Legal</span>
            <Link href={routes.terms} style={foot}>Terms of Service</Link>
            <Link href={routes.privacy} style={foot}>Privacy Policy</Link>
            <Link href={routes.refund} style={foot}>Refund Policy</Link>
            <Link href={routes.contact} style={foot}>Contact</Link>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--space-4)', flexWrap: 'wrap', borderTop: '1px solid var(--border-subtle)', paddingTop: 'var(--space-5)' }}>
          {/* The business holds the copyright, not the product — Spruce AEO,
              read from legalData.js so it is the name the legal pages use. */}
          <span className="sp-caption">© 2026 {legal.business}</span>
          <span className="sp-caption">{quoted.engine_coverage}</span>
        </div>
      </div>
    </footer>
  )
}

// For the standalone pages, which have no layout of their own. Never reads its
// argument: Inertia 3 calls a function layout with the page's props first.
export const withSiteFooter = (page) => <>{page}<SiteFooter /></>
